import { ChilizLogoRounded, encode, NetworkType, UnknownDApp, useFormatter, useIntl } from '@capsicum/common';
import { BorderedSection, CopyToClipboard, Divider, Image, InfoTooltip, NetworkIcon, Text } from '@capsicum/common-web';
import { useNCWBalance } from '@capsicum/socios-core';
import { WalletConnectConfirmationActions } from '@socios-blk/core';
import React, { type ReactNode } from 'react';
import styled from 'styled-components';

type SendTransactionModalProps = {
	action?: string;
	origin?: string;
	payload: {
		data?: string;
		from?: string;
		gasFeeEstimate?: number;
		origin?: string;
		to?: string;
		value?: number | string;
	};
	unknown?: boolean;
};

const GOVERNANCE_ORIGIN = 'https://governance.chilizchain.com';

export const SendTransactionModal = ({ action, origin, payload, unknown }: SendTransactionModalProps) => {
	const { $t } = useIntl();
	const { formatCrypto } = useFormatter();
	const { chilizBalance, formatCHZtoFiat } = useNCWBalance();
	const isGovernance = origin?.startsWith(GOVERNANCE_ORIGIN);

	const WalletConnectActions: Record<string, string> = {
		APPROVE: $t('ncWallet.walletConnect.actions.approve'),
		AUTH_DAPP: $t('ncWallet.walletConnect.actions.authDApp'),
		CLAIM: $t('ncWallet.walletConnect.actions.claim'),
		DECREASE_ALLOWANCE: $t('ncWallet.walletConnect.actions.decreaseAllowance'),
		INCREASE_ALLOWANCE: $t('ncWallet.walletConnect.actions.increaseAllowance'),
		SEND_TRANSACTION: $t('ncWallet.walletConnect.actions.sendTransaction'),
		SIGN_MESSAGE: $t('ncWallet.walletConnect.actions.signMessage'),
		STAKE: $t('ncWallet.walletConnect.actions.stake'),
		TRANSFER: $t('ncWallet.walletConnect.actions.transfer'),
		TRANSFER_FROM: $t('ncWallet.walletConnect.actions.transferFrom'),
		UNSTAKE: $t('ncWallet.walletConnect.actions.unstake')
	};

	const { data, from, gasFeeEstimate, to, value } = payload;
	const valueGas = (gasFeeEstimate as any)?.value ? (gasFeeEstimate as any)?.value : gasFeeEstimate;
	const gasFeeFiat = valueGas && formatCHZtoFiat(valueGas);

	const SectionRow = ({ rightNode, text }: { rightNode: ReactNode; text: ReactNode | string }) => (
		<S.Row>
			<Text variant="text3">{text}</Text>
			<S.RightNodeWrapper>{rightNode}</S.RightNodeWrapper>
		</S.Row>
	);

	return (
		<S.MainWrapper>
			{isGovernance ? (
				<S.AppIcon alt="Chiliz Logo" height={48} src={ChilizLogoRounded} width={48} />
			) : (
				unknown && <S.AppIcon alt="Unknown App Logo" height={48} src={UnknownDApp} width={48} />
			)}
			<S.Title testID="wallet-connect-transaction-modal-action-title" variant="title5">
				{unknown
					? $t('ncWallet.walletConnect.transactionModal.unknown')
					: WalletConnectActions[action as string]}
			</S.Title>
			<S.OriginText testID="wallet-connect-transaction-modal-origin-text" variant="text2">
				{origin}
			</S.OriginText>
			<S.BalanceWrapper data-testid="wallet-connect-transaction-modal-balance">
				<Image alt="Chiliz Logo" height={20} src={ChilizLogoRounded} width={20} />
				<Text variant="text2">
					{`${$t('ncWallet.walletConnect.transactionModal.balance')} ${formatCrypto(chilizBalance?.usableAmount || 0, chilizBalance?.shortCode || '')}`}
				</Text>
				<S.SecondaryText variant="text2">{`(${encode('doubleTilde')}${formatCHZtoFiat(chilizBalance?.usableAmount || 0)})`}</S.SecondaryText>
			</S.BalanceWrapper>
			<S.BodyWrapper>
				{/*Amount Card*/}
				{value && (
					<S.StyledBorderedSection
						testID="wallet-connect-transaction-modal-transaction-section-amount"
						title=""
					>
						<SectionRow
							rightNode={
								<S.ValueText testID="wallet-connect-transaction-modal-amount" variant="title6">
									{/* TODO: Eventually once we get more ABIS we will need to change this as we will be able to show amounts of tokens */}
									<Image alt="Chiliz Logo" height={25} src={ChilizLogoRounded} width={25} />
									{`${value} ${$t('payments.label.chilizShortCode')}`}
								</S.ValueText>
							}
							text={$t('ncWallet.walletConnect.transactionModal.amount')}
						/>
					</S.StyledBorderedSection>
				)}
				{/*Network and Network Fee Card*/}
				<S.StyledBorderedSection testID="wallet-connect-transaction-modal-transaction-section-fee" title="">
					<SectionRow
						rightNode={
							<>
								<NetworkIcon network={NetworkType.CC2} />
								{$t('info.chilizChain')}
							</>
						}
						text={$t('ncWallet.walletConnect.transactionModal.network')}
					/>
					{valueGas && gasFeeFiat && <Divider orientation="horizontal" />}
					{valueGas && gasFeeFiat && (
						<SectionRow
							rightNode={
								<S.GasTextBlock data-testid="wallet-connect-transaction-modal-network-fee-amount">
									<S.ValueText variant="text4">
										{`${valueGas} ${$t('payments.label.chilizShortCode')}`}
									</S.ValueText>
									<S.SecondaryText variant="text5">{`(${encode('doubleTilde')}${gasFeeFiat})`}</S.SecondaryText>
								</S.GasTextBlock>
							}
							text={
								<S.TooltipWRow>
									{$t('ncWallet.walletConnect.transactionModal.networkFee')}
									<S.TooltipWrapper>
										<InfoTooltip testID="wallet-connect-modal-transaction-tooltip-fee">
											{$t('ncWallet.walletConnect.transactionModal.feeTooltip')}
										</InfoTooltip>
									</S.TooltipWrapper>
								</S.TooltipWRow>
							}
						/>
					)}
				</S.StyledBorderedSection>
				{/* From To Address Card */}
				<S.StyledBorderedSection
					testID="wallet-connect-transaction-modal-transaction-section-fromToAddress"
					title=""
				>
					{from && action !== WalletConnectConfirmationActions.UNSTAKE && (
						<SectionRow
							rightNode={
								<CopyToClipboard
									label={$t('ncWallet.walletConnect.confirmationModal.address')}
									testID="copy-transaction-from-address"
									value={from}
								/>
							}
							text={$t('ncWallet.walletConnect.transactionModal.from')}
						/>
					)}
					{from && action !== WalletConnectConfirmationActions.UNSTAKE && to && (
						<Divider orientation="horizontal" />
					)}
					{to && (
						<SectionRow
							rightNode={
								<CopyToClipboard
									label={$t('ncWallet.walletConnect.confirmationModal.address')}
									testID="copy-transaction-to-address"
									value={to}
								/>
							}
							text={$t('ncWallet.walletConnect.transactionModal.to')}
						/>
					)}
				</S.StyledBorderedSection>
				{data && (
					<S.StyledBorderedSection
						testID="wallet-connect-transaction-modal-transaction-section-data"
						title=""
					>
						<SectionRow
							rightNode={
								<CopyToClipboard
									label={$t('ncWallet.walletConnect.confirmationModal.address')}
									testID="copy-transaction-data"
									value={data}
								/>
							}
							text={$t('ncWallet.walletConnect.transactionModal.data')}
						/>
					</S.StyledBorderedSection>
				)}
			</S.BodyWrapper>
		</S.MainWrapper>
	);
};

namespace S {
	export const MainWrapper = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	`;
	export const BodyWrapper = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: center;
		width: 100%;
	`;

	export const AppIcon = styled(Image)`
		display: block;
		margin: 0 auto ${({ theme }) => theme.sizing.xsmall};
	`;

	export const Title = styled(Text)`
		display: block;
		text-align: center;
		margin-bottom: ${({ theme }) => theme.sizing.xxsmall};
	`;

	export const ValueText = styled(Text)`
		display: flex;
		justify-content: center;
		text-align: center;
		align-items: center;
		gap: ${({ theme }) => theme.sizing.xxxsmall};
	`;
	export const GasTextBlock = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: center;
		text-align: center;
		align-items: flex-end;
		gap: ${({ theme }) => theme.sizing.xxxsmall};
	`;

	export const BalanceWrapper = styled.div`
		display: inline-flex;
		justify-content: center;
		text-align: center;
		align-items: center;
		gap: ${({ theme }) => theme.sizing.xxxsmall};
		border-color: ${({ theme }) => theme.colors.borders.main.divider};
		border-style: ${({ theme }) => theme.borderStyles.divider.style};
		border-width: ${({ theme }) => theme.borderStyles.divider.width};
		${({ theme }) => theme.border.radius.circle};
		margin-bottom: ${({ theme }) => theme.sizing.small};
		padding: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const OriginText = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		text-align: center;
		margin-bottom: ${({ theme }) => theme.sizing.medium};
	`;
	export const SecondaryText = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		text-align: center;
	`;
	export const StyledBorderedSection = styled(BorderedSection)`
		margin-bottom: ${({ theme }) => theme.sizing.small};
	`;
	export const Row = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		padding: ${({ theme }) => theme.sizing.xxsmall} 0;
	`;

	export const RightNodeWrapper = styled.div`
		${({ theme }) => theme.fonts.text.text4};
		margin-left: ${({ theme }) => theme.sizing.xxxsmall};
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.sizing.xxxsmall};
	`;
	export const TooltipWrapper = styled.div`
		position: relative;
		z-index: 13;
		text-align: left;
	`;
	export const TooltipWRow = styled.div`
		display: flex;
		gap: ${({ theme }) => theme.sizing.xxxsmall};
		align-items: center;
	`;
}
